/* External dependencies */
import PropTypes from "prop-types";
import React from "react";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";

/* Internal dependencies */
import { Button, Modal } from "@yoast/ui-library";
import * as styles from "./ComposerHelpStyles.module.scss";
import Link from "../Link";

const messages = defineMessages( {
	header: {
		id: "composer-help.header",
		defaultMessage: "How to install {product} using composer",
	},
	introduction: {
		id: "composer-help.introduction",
		defaultMessage: "Composer is a tool used by many developers to install and update plugins. " +
			"Through MyYoast you can use Composer to get easy access to your premium plugins. " +
			"Follow the instructions below to get started!",
	},
	tokenInstruction: {
		id: "composer-help.token-instruction",
		defaultMessage: "First, create a personal access token with the Composer download permission on the " +
			"{link} page. Copy the token when it is shown — you will only see it once.",
	},
	patLinkLabel: {
		id: "composer-help.pat-link-label",
		defaultMessage: "Personal access tokens",
	},
	registerTokenInstruction: {
		id: "composer-help.register-token",
		defaultMessage: "Register your token with composer by running the command below, " +
			"replacing {placeholder} with your token:",
	},
	registerRepositoryInstruction: {
		id: "composer-help.register-repository",
		defaultMessage: "You can then add our secure repository by running the following command:",
	},
	requireInstruction: {
		id: "composer-help.require",
		defaultMessage: "Now you can install {product} by running:",
	},
	wpAutoloadInstruction: {
		id: "composer-help.require-autoload",
		defaultMessage: "In order to use {product} with Composer you will have to require Composer's {autoload} file. " +
			"We recommend adding this to your {wpConfig} file.",
	},
	gotIt: {
		id: "gettingStarted.gotIt",
		defaultMessage: "Got it",
	},
} );


/**
 * Renders the ComposerHelp component.
 *
 * @param {Object} props The props to use.
 * @param {Function} props.onClose The function to execute when the got it button is clicked.
 *
 * @returns {ReactElement} A react component rendering the composer help modal.
 */
function ComposerHelp( props ) {
	return (
		<>
			<Modal.Title as="h2" size="2">
				<FormattedMessage { ...messages.header } values={ { product: props.productName } } />
			</Modal.Title>
			<div className={ styles.contentWrapper }>
				<p>
					<FormattedMessage { ...messages.introduction } />
				</p>

				<div>
					<FormattedMessage
						{ ...messages.tokenInstruction }
						values={ {
							link: (
								<Link to="/personal-access-tokens" onClick={ props.onClose }>
									<FormattedMessage { ...messages.patLinkLabel } />
								</Link>
							),
						} }
					/>
				</div>

				<div>
					<FormattedMessage
						{ ...messages.registerTokenInstruction }
						values={ { placeholder: <code>YOUR_TOKEN</code> } }
					/>
					<pre>composer config -g http-basic.my.yoast.com token YOUR_TOKEN</pre>
				</div>
				<div>
					<FormattedMessage { ...messages.registerRepositoryInstruction } />
					<pre>composer config repositories.my-yoast composer https://my.yoast.com/packages/</pre>
				</div>
				<div>
					<FormattedMessage
						{ ...messages.requireInstruction }
						values={ { product: props.productName } }
					/>
					<pre>composer require { props.composerHandle }</pre>
				</div>
				{ props.isWordPressPlugin &&
					<div className={ styles.combinedWrapper }>
						<FormattedMessage
							{ ...messages.wpAutoloadInstruction }
							values={ {
								product: props.productName,
								autoload: <pre>vendor/autoload.php</pre>,
								wpConfig: <pre>wp-config.php</pre>,
							} }
						/>
					</div>
				}
			</div>

			<div className={ styles.buttonWrapper }>
				<Button onClick={ props.onClose } variant="secondary">
					<FormattedMessage { ...messages.gotIt } />
				</Button>
			</div>
		</>
	);
}

ComposerHelp.propTypes = {
	onClose: PropTypes.func.isRequired,
	productName: PropTypes.string.isRequired,
	composerHandle: PropTypes.string.isRequired,
	isWordPressPlugin: PropTypes.bool,
};

ComposerHelp.defaultProps = {
	isWordPressPlugin: false,
};


export default injectIntl( ComposerHelp );
